import { createSpeechRecognitionSession } from '#pplx/frontend/apps/ask/multimodal/speech-recognition/providers/soniox/session.js';
import { IDLE_STATE, isTerminalState, } from '#pplx/frontend/apps/ask/multimodal/speech-recognition/providers/soniox/state.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { createStore } from 'zustand/vanilla';
// Single-file events: defined here at their only call site rather than in a shared package.
const startTranscriptionEvent = EVENT_PAYLOAD;
const stopTranscriptionEvent = EVENT_PAYLOAD;
const detectSupport = () => typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.enumerateDevices === 'function' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function';
/** Module-level so ids stay unique across every composer on the page (used for log correlation). */
let nextSessionId = 1;
export const createTranscriptionStore = (initial) => createStore()((set) => {
    let config = initial;
    let current = null;
    let currentStartedAt = 0;
    /**
     * Projects the current session's transitions. The identity check exists for
     * `dispose()`, which clears `current` before stopping the old session so its
     * final transition is swallowed here.
     */
    const observe = (session, next, prev) => {
        if (session !== current)
            return;
        set({ state: next });
        if (next.kind === 'active' && prev.kind === 'connecting') {
            config.callbacks?.onStart?.();
        }
        if (isTerminalState(next)) {
            config.track(stopTranscriptionEvent({
                name: 'stop transcription',
                data: {
                    durationMs: Date.now() - currentStartedAt,
                    reason: config.reason,
                    cause: next.kind === 'failed' ? next.error.kind : next.cause,
                },
            }));
            config.callbacks?.onStop?.();
        }
    };
    return {
        state: IDLE_STATE,
        isSupported: detectSupport(),
        configure: (next) => {
            config = next;
        },
        start: () => {
            if (current && !isTerminalState(current.state)) {
                logger.info('[transcription] start ignored: session already active', {
                    id: current.id,
                    state: current.state.kind,
                });
                return Promise.resolve();
            }
            const session = createSpeechRecognitionSession({
                id: nextSessionId++,
                reason: config.reason,
                source: config.source,
                silenceThreshold: config.silenceThreshold,
                silenceDuration: config.silenceDuration,
                onSilence: () => config.callbacks?.onSilence?.(),
                onStateChange: (next, prev) => observe(session, next, prev),
            });
            current = session;
            currentStartedAt = Date.now();
            config.track(startTranscriptionEvent({ name: 'start transcription', data: { reason: config.reason } }));
            return session.start();
        },
        stop: () => current?.stop(),
        dispose: () => {
            const session = current;
            current = null;
            session?.stop();
        },
    };
});
//# sourceMappingURL=transcriptionStore.js.map